import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import axios from '../../../../library/axios';
import {
  handleJobQueuedSuccess,
  handleJobRequestError,
} from '../../../../helpers/cpanelJobFeedback';
import { USER_SCOPE_CENTRAL, CENTRAL_ROLES, ACTIVE_OPTIONS } from './constants';
import { hasValidationErrors, validateCentralForm, validateHospitalForm } from './validation';

const CreateCpanelUserComp = ({
  scope,
  tenantSlug,
  tenants = [],
  meta,
  close,
  fetch,
  onTenantChange,
}) => {
  const isCentral = scope === USER_SCOPE_CENTRAL;
  const [selectedTenant, setSelectedTenant] = useState(tenantSlug || '');
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [namaLengkap, setNamaLengkap] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('operator');
  const [idGroups, setIdGroups] = useState('');
  const [idDivision, setIdDivision] = useState('');
  const [nik, setNik] = useState('');
  const [jabatan, setJabatan] = useState('');
  const [active, setActive] = useState('YES');
  const [errors, setErrors] = useState({});

  const handleCreate = async (event) => {
    event?.preventDefault();
    const nextErrors = isCentral
      ? validateCentralForm({ username, fullName, password, role })
      : validateHospitalForm({ username, namaLengkap, email, idGroups, password });
    setErrors(nextErrors);
    if (!isCentral && !selectedTenant) {
      setErrors({ tenantSlug: 'Tenant wajib dipilih.' });
      return;
    }
    if (hasValidationErrors(nextErrors)) return;

    setLoading(true);
    const payload = isCentral
      ? { scope, username: username.trim(), password, fullName: fullName.trim(), email: email.trim(), role, active }
      : {
          scope,
          tenantSlug: selectedTenant,
          username: username.trim(),
          password,
          namaLengkap: namaLengkap.trim(),
          email: email.trim(),
          idGroups,
          idDivision: idDivision || null,
          nik: nik.trim() || null,
          jabatan: jabatan.trim() || '-',
          active,
        };

    try {
      const res = await axios.post('/cpanel/users/create', payload);
      await handleJobQueuedSuccess({
        response: res,
        actionLabel: isCentral ? 'Pembuatan pengguna CPanel' : 'Pembuatan pengguna rumah sakit',
        close,
        fetch,
      });
    } catch (err) {
      handleJobRequestError(err, 'Gagal memulai pembuatan pengguna.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form noValidate onSubmit={handleCreate}>
      <div className="mb-3">
        <label className="form-label required">Username</label>
        <input
          className={`form-control${errors.username ? ' is-invalid' : ''}`}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        {errors.username ? <div className="invalid-feedback">{errors.username}</div> : null}
      </div>
      <div className="mb-3">
        <label className="form-label required">Password</label>
        <input
          type="password"
          className={`form-control${errors.password ? ' is-invalid' : ''}`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {errors.password ? <div className="invalid-feedback">{errors.password}</div> : null}
      </div>
      {isCentral ? (
        <>
          <div className="mb-3">
            <label className="form-label required">Nama Lengkap</label>
            <input
              className={`form-control${errors.fullName ? ' is-invalid' : ''}`}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            {errors.fullName ? <div className="invalid-feedback">{errors.fullName}</div> : null}
          </div>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label required">Role</label>
            <select
              className={`form-select${errors.role ? ' is-invalid' : ''}`}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {CENTRAL_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3">
            <label className="form-label required">Tenant / Rumah Sakit</label>
            <select
              className={`form-select${errors.tenantSlug ? ' is-invalid' : ''}`}
              value={selectedTenant}
              onChange={(e) => {
                setSelectedTenant(e.target.value);
                onTenantChange?.(e.target.value);
              }}
            >
              <option value="">Pilih tenant</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.slug} - {t.description}
                </option>
              ))}
            </select>
            {errors.tenantSlug ? <div className="invalid-feedback">{errors.tenantSlug}</div> : null}
          </div>
          <div className="mb-3">
            <label className="form-label required">Nama Lengkap</label>
            <input
              className={`form-control${errors.namaLengkap ? ' is-invalid' : ''}`}
              value={namaLengkap}
              onChange={(e) => setNamaLengkap(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label required">Email</label>
            <input
              className={`form-control${errors.email ? ' is-invalid' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label required">Role (Group)</label>
            <select
              className={`form-select${errors.idGroups ? ' is-invalid' : ''}`}
              value={idGroups}
              onChange={(e) => setIdGroups(e.target.value)}
            >
              <option value="">Pilih role</option>
              {(meta?.groups || []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.namaGroup}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Divisi</label>
            <select className="form-select" value={idDivision} onChange={(e) => setIdDivision(e.target.value)}>
              <option value="">- Otomatis / kosong -</option>
              {(meta?.divisions || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.namaDivision}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">NIK</label>
            <input className="form-control" value={nik} onChange={(e) => setNik(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Jabatan</label>
            <input className="form-control" value={jabatan} onChange={(e) => setJabatan(e.target.value)} />
          </div>
        </>
      )}
      <div className="mb-4">
        <label className="form-label">Status</label>
        <select className="form-select" value={active} onChange={(e) => setActive(e.target.value)}>
          {ACTIVE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="light" onClick={close} disabled={loading}>
          Batal
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Memproses...' : 'Simpan'}
        </Button>
      </div>
    </form>
  );
};

export default CreateCpanelUserComp;
